import { ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/page-header';
import { ThemeToggle } from '@/components/theme-toggle';
import { useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { useFeatures } from '@/lib/features';
import { JobTypesPanel } from '@/features/job-types/job-types-panel';
import { BusinessPanel } from './business-panel';
import { FeaturesPanel } from './features-panel';
import { EmailForwardingCard } from './email-forwarding-card';
import { GreenInvoiceCard } from './green-invoice-card';
import { WhatsAppCard } from './whatsapp-card';
import { IntegrationsPanel } from './integrations-panel';
import { ProfilePanel } from './profile-panel';
import { TeamPanel } from './team-panel';
import { BillingPanel } from './billing/billing-panel';

const TABS = ['profile', 'business', 'features', 'job-types', 'appearance', 'team', 'integrations', 'billing'];
/** מנהל: הפרופיל שלו, ומצב המנוי בצפייה בלבד (השרת מחזיר canManage=false). */
const MANAGER_TABS = ['profile', 'billing'];

export function SettingsPage() {
  const { t } = useTranslation('settings');
  const { can } = useAuth();
  const features = useFeatures();
  // /settings/business, /settings/integrations — קישור ישיר ללשונית.
  const { tab } = useParams({ strict: false });
  const initialTab = tab && TABS.includes(tab) ? tab : 'profile';

  // הסתרת UI בלבד. ההרשאה נאכפת בשרת ב-RolesGuard על כל בקשה
  // (`@Roles(UserRole.OWNER)` על נתיבי ה-integrations) — מי שינווט
  // לכאן ידנית יקבל 403 מהשרת, לא מכאן. מי שאינו אדמין (עריכה, צפייה
  // בלבד) רואה רק את הפרופיל שלו — סיסמה ואבטחת החשבון.
  if (!can('OWNER')) {
    if (!can('MANAGER', 'VIEWER')) {
      return (
        <div className="mx-auto max-w-3xl">
          <EmptyState
            icon={ShieldAlert}
            title={t('page.noAccessTitle')}
            description={t('page.noAccessDescription')}
          />
        </div>
      );
    }
    if (can('MANAGER')) {
      const managerTab = tab && MANAGER_TABS.includes(tab) ? tab : 'profile';
      return (
        <div className="mx-auto max-w-3xl space-y-5">
          <PageHeader title={t('page.title')} />
          <Tabs defaultValue={managerTab} key={managerTab}>
            <TabsList aria-label={t('page.tabsLabel')}>
              <TabsTrigger value="profile" className="min-h-11">{t('page.tabs.profile')}</TabsTrigger>
              <TabsTrigger value="billing" className="min-h-11">{t('page.tabs.billing')}</TabsTrigger>
            </TabsList>
            <TabsContent value="profile">
              <ProfilePanel />
            </TabsContent>
            <TabsContent value="billing">
              <BillingPanel />
            </TabsContent>
          </Tabs>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-3xl space-y-5">
        <PageHeader title={t('page.tabs.profile')} />
        <ProfilePanel />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <PageHeader title={t('page.title')} description={t('page.description')} />

      <Tabs defaultValue={initialTab} key={initialTab}>
        <TabsList aria-label={t('page.tabsLabel')}>
          <TabsTrigger value="profile" className="min-h-11">{t('page.tabs.profile')}</TabsTrigger>
          <TabsTrigger value="business" className="min-h-11">{t('page.tabs.business')}</TabsTrigger>
          <TabsTrigger value="features" className="min-h-11">{t('page.tabs.features')}</TabsTrigger>
          <TabsTrigger value="job-types" className="min-h-11">{t('page.tabs.jobTypes')}</TabsTrigger>
          <TabsTrigger value="appearance" className="min-h-11">{t('page.tabs.appearance')}</TabsTrigger>
          <TabsTrigger value="team" className="min-h-11">{t('page.tabs.team')}</TabsTrigger>
          <TabsTrigger value="integrations" className="min-h-11">{t('page.tabs.integrations')}</TabsTrigger>
          <TabsTrigger value="billing" className="min-h-11">{t('page.tabs.billing')}</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <ProfilePanel />
        </TabsContent>

        <TabsContent value="business">
          <BusinessPanel />
        </TabsContent>

        <TabsContent value="features">
          <FeaturesPanel />
        </TabsContent>

        <TabsContent value="job-types">
          <JobTypesPanel />
        </TabsContent>

        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle>{t('appearance.title')}</CardTitle>
              <CardDescription>{t('appearance.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ThemeToggle />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team">
          <TeamPanel />
        </TabsContent>

        <TabsContent value="integrations" className="space-y-5">
          <EmailForwardingCard />
          <IntegrationsPanel />
          {features.has('invoicing') ? <GreenInvoiceCard /> : null}
          <WhatsAppCard />
        </TabsContent>

        <TabsContent value="billing">
          <BillingPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
